<script>
   /*************************************************************/
   /* PlotDistance for PLSDA results                            */
   /* ----------------------------                              */
   /* shows plot with score and orthogonal distances            */
   /* and acceptance areas                                      */
   /*************************************************************/

   import { Lines, ColormapLegend } from 'svelte-plots-basic/2d';
   import {plsmodel_get, plsres_get } from '@mdatools-apps/wasm/pls.js';
   import { max } from 'mdatools/stat';
   import { getColors, getClasses, CLASS_COLORS  } from './Controls.svelte.js';
   import { csvRow, downloadCSV } from '@mdatools-apps/shared/utils';

   import { MARKERS, MAX_LABEL_SYMBOLS, getDistanceAxisLabel,
      getDistanceCoords, getDistanceLimitCoordinates,
       PNG_WIDTH, PNG_HEIGHT, PNG_RES, MARGINS } from '@mdatools-apps/shared/plots/Defaults.js';
   import PlotSeries from '@mdatools-apps/shared/plots/PlotSeries.svelte';
   import Axes from '@mdatools-apps/shared/plots/Axes.svelte';
   import { plotSize, legendNames } from '@mdatools-apps/shared/plots/layout.js';

   let {
      params,                        // JSON with main plot parameters
      callbacks,                     // JSON with all callback methods
      props,                         // JSON with main properties (model, results, dataset)
      selected,                      // ID of selected object
   } = $props();

   // get the parameters needed for the plot
   let model = $derived(props && props.model ? props.model : null);
   let resobj = $derived(props && props.res && params.resID ? props.res[params.resID] : null);
   let classresobj = $derived(props && props.classres && params.resID ? props.classres[params.resID] : null);

   // constant plot parameters
   const LINE_COLOR = '#909090';    // color of the limits lines


   // get Nu and u0 for specific type of limits
   const N = $derived.by(() => {
      if (!model) return null;
      return params.limType === 'classic' ?
      {h: plsmodel_get(model, 'cNh'), q: plsmodel_get(model, 'cNq'), z: plsmodel_get(model, 'cNz')} :
      {h: plsmodel_get(model, 'rNh'), q: plsmodel_get(model, 'rNq'), z: plsmodel_get(model, 'rNz')}
   });

   const u0 = $derived.by(() => {
      if (!model) return null;
      return params.limType === 'classic' ?
      {h: plsmodel_get(model, 'ch0'), q: plsmodel_get(model, 'cq0'), z: plsmodel_get(model, 'cz0')} :
      {h: plsmodel_get(model, 'rh0'), q: plsmodel_get(model, 'rq0'), z: plsmodel_get(model, 'rz0')}
   });

   // get Nu and u0 for selected number of components
   const Na = $derived(N ? {h: N.h[props.ncomp - 1], q: N.q[props.ncomp - 1], z: N.z[props.ncomp - 1]} : null);
   const u0a = $derived(u0 ? {h: u0.h[props.ncomp - 1], q: u0.q[props.ncomp - 1], z: u0.z[props.ncomp - 1]} : null);

   // get Nu and u0 for given axes
   const Nx = $derived(Na ? (params.distType === 'h/q' ? Na.h : Na.h + Na.q) : null);
   const Ny = $derived(Na ? (params.distType === 'h/q' ? Na.q : Na.z) : null);

   const u0x = $derived(u0a ? (params.distType === 'h/q' ? u0a.h : Na.h + Na.q) : null);
   const u0y = $derived(u0a ? (params.distType === 'h/q' ? u0a.q : u0a.z) : null);


   // get conf limits
   const ecl = $derived(params.alpha ? 1 - params.alpha/100 : 0.95);
   const ocl = $derived(params.gamma ? 1 - params.gamma/100 : 0.99);

   // get coordinates for limits
   let els = $derived(Na ? getDistanceLimitCoordinates(ecl, Nx, Ny, params.transType) : null);
   let ols = $derived(Na ? getDistanceLimitCoordinates(Math.pow(ocl, 1/model.nrows), Nx, Ny, params.transType) : null);

   let h = $derived(resobj ? plsres_get(resobj, 'hdist', props.ncomp) : null);
   let q = $derived(resobj ? plsres_get(resobj, 'qdist', props.ncomp) : null);
   let z = $derived(resobj ? plsres_get(resobj, 'zdist', props.ncomp) : null);
   let f = $derived(h && q ? h.map((h, i) => h / u0a.h * Na.h + q[i] / u0a.q * Na.q) : null);

   let x = $derived(params.distType === 'h/q' ? h : f);
   let y = $derived(params.distType === 'h/q' ? q : z);

   const xv = $derived(x ? getDistanceCoords(x, Nx, u0x, params.transType) : null)
   const yv = $derived(y ? getDistanceCoords(y, Ny, u0y, params.transType) : null)

   const labels = $derived(resobj ? resobj.objlabels.map(v => v.slice(0, MAX_LABEL_SYMBOLS)) : null);
   let colors = $derived(getColors(classresobj));
   let pch = $derived(MARKERS[params.resID]);

   // derive plot parameters
   let title = $derived('Distances (A = ' + props.ncomp + ')');
   let xLabel = $derived(getDistanceAxisLabel(params.distType[0], params.transType));
   let yLabel = $derived(getDistanceAxisLabel(params.distType[2], params.transType));

   // reactive expression to compute x-axis limits
   let limX = $derived([0, Math.max(xv.max, max(ols.x)) * 1.1]);
   let limY = $derived([0, Math.max(yv.max, max(ols.y)) * 1.2]);

   // the drawn size of the plot, for the class names cut to fit their legend cells
   let size = $state([0, 0]);
   const legendLabels = $derived(classresobj && classresobj.ind ?
      legendNames([classresobj.classNames[1], classresobj.classNames[0]], size, MARGINS, true) : null);

   // name of the image and CSV files
   const fileName = 'plot-distance';

   // method for creating CSV version of the plot data, with the boundaries as drawn. the labels
   // are whole, not cut as on the plot
   function downloadCSVCallback() {
      const { sep, dec } = props.data[params.resID];
      const classes = getClasses(classresobj);
      let out = csvRow(['Set', 'Labels', 'Classes', xLabel, yLabel], sep, dec);
      for (let i = 0; i < resobj.objlabels.length; i++) {
         out += csvRow([params.resID, resobj.objlabels[i], classes[i], xv.values.v[i], yv.values.v[i]],
            sep, dec);
      }
      for (const [name, b] of [['extreme boundary', els], ['outlier boundary', ols]]) {
         if (!b) continue;
         for (let i = 0; i < b.x.length; i++) {
            out += csvRow([name, '', '', b.x.v[i], b.y.v[i]], sep, dec);
         }
      }
      downloadCSV(out, fileName);
   }

</script>

{#if xv && yv}
<!-- 'display: contents' leaves the layout to Axes -->
<div style="display: contents" {@attach plotSize(v => size = v)}>
<Axes
   {title} {limX} {limY} {xLabel} {yLabel} {fileName} {downloadCSVCallback}
   downloadLinks="hover"
   margins={MARGINS}
   pngWidth={PNG_WIDTH}
   pngHeight={PNG_HEIGHT}
   pngRes={PNG_RES}

   >

   <!-- limits  -->
   {#if els }
   <Lines xValues={els.x} yValues={els.y} lineColor={LINE_COLOR} lineType={2} />
   {/if}

   {#if ols}
   <Lines xValues={ols.x} yValues={ols.y} lineColor={LINE_COLOR} lineType={3} />
   {/if}

   <!-- data points  -->
   <PlotSeries
      {selected}
      {callbacks}
      {labels}
      {colors}
      marker={pch}
      xValues={xv.values}
      yValues={yv.values}
      plotType='p'
      showLabels={params.showLabels}
   />

   <!-- colormap legend-->
   {#if classresobj && classresobj.ind}
    <ColormapLegend colmap={[CLASS_COLORS[1], CLASS_COLORS[0]]} breaks={[0, 1, 2]} labels={legendLabels}/>
   {/if}


</Axes>
</div>
{/if}
